import { useState, useRef, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { useNavigate } from 'react-router-dom';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <button
          onClick={() => navigate('/')}
          className="text-xl font-heading font-semibold text-foreground-900 tracking-tight whitespace-nowrap cursor-pointer bg-transparent border-none p-0"
        >
          CSSSpreadsheet
        </button>

        <div className="hidden md:flex items-center gap-6">
          <button
            onClick={() => navigate('/')}
            className="text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
          >
            Home
          </button>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              Categories
              <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-xs"></i>
              </span>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 max-h-[460px] overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors flex items-center gap-3 cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-8 h-8 flex-shrink-0 rounded-lg overflow-hidden">
                      <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                    </span>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
          >
            Sheets
          </a>

          <button
            onClick={() => navigate('/cssbuy-sheet-guide')}
            className="text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
          >
            Shopping Guide
          </button>
        </div>

        <div className="flex items-center gap-3">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            cssbuy sheet
          </a>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg text-foreground-700`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 max-h-[70vh] overflow-y-auto">
          <div className="px-4 py-3">
            <button
              onClick={() => { setMobileMenuOpen(false); navigate('/'); }}
              className="block w-full text-left px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg whitespace-nowrap cursor-pointer"
            >
              Home
            </button>
          </div>

          <div className="px-4 pb-2">
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide px-3 py-1">Categories</p>
          </div>
          <div className="px-4 pb-3 grid grid-cols-2 gap-1.5">
            {categories.map((cat) => (
              <button
                key={cat.name}
                onClick={() => handleCategorySelect(cat.url)}
                className="flex items-center gap-2 px-2.5 py-2 text-xs text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <span className="w-7 h-7 flex-shrink-0 rounded-md overflow-hidden">
                  <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                </span>
                <span className="leading-tight">{cat.name}</span>
              </button>
            ))}
          </div>

          <div className="border-t border-background-200 px-4 py-3 flex flex-col gap-1">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg whitespace-nowrap"
            >
              Sheets
            </a>
            <button
              onClick={() => { setMobileMenuOpen(false); navigate('/cssbuy-sheet-guide'); }}
              className="w-full text-left px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg whitespace-nowrap"
            >
              Shopping Guide
            </button>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3 py-2.5 text-sm text-center bg-primary-500 text-background-50 rounded-full font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              cssbuy sheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}